import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, BarChart3, Download, BookOpen, Sparkles, Pin, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Markdown } from "@/components/agyin/Markdown";

const box = "mt-2 rounded-lg border p-3 text-xs";
const border = { borderColor: "var(--po-border)" };
export const UPGRADE_TEXT = "Upgrade to Agyin Pro to unlock proactive insights, drafts, and bulk actions.";

export function ProLocked({ testId, onNavigate, text = UPGRADE_TEXT }) {
  const navigate = useNavigate();
  return (
    <div data-testid={testId} className="flex items-start gap-2 rounded-lg border border-dashed bg-slate-50 px-3 py-2.5 text-xs text-slate-500 opacity-80" style={border}>
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span>{text}{" "}<button data-testid={`${testId}-upgrade-link`} onClick={() => { onNavigate?.(); navigate("/app/billing"); }} className="font-semibold text-[#13294B] underline">View plans and upgrade</button></span>
    </div>);
}

export function UsageNotice({ usage }) {
  if (!usage?.notice) return null;
  return <div data-testid={`agyin-usage-${usage.level}`} className={`border-b px-4 py-2 text-xs ${usage.level === "limit" ? "po-tone-danger" : "po-tone-warning"}`} style={border}>{usage.notice} ({usage.used} of {usage.allowance})</div>;
}

const fmtV = (v) => (v == null || v === "" ? "—" : typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v));

function downloadCsv(r) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [r.columns.map((c) => esc(c.label)).join(","), ...r.rows.map((row) => r.columns.map((c) => esc(row[c.key])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${r.period.from}-to-${r.period.to}.csv`;
  a.click();
  api.post("/agyin/log", { action: "analytics_query_exported", detail: { title: r.title, rows: r.rows.length } }).catch(() => {});
}

export function AnalyticsCard({ a }) {
  const r = a.result;
  if (!r) return null;
  return (
    <div className={box} style={border} data-testid="agyin-analytics-card">
      <div className="flex items-center gap-1.5 font-semibold text-slate-900"><BarChart3 className="h-3.5 w-3.5" /><span data-testid="agyin-analytics-title">{r.title}</span></div>
      <div className="text-slate-500">{r.period.from} to {r.period.to} · {r.currency}</div>
      <div className="mt-2 grid grid-cols-2 gap-2" data-testid="agyin-analytics-figures">{r.key_figures.map((f) => <div key={f.label} className="rounded-md bg-slate-50 px-2 py-1.5"><div className="text-[10px] uppercase tracking-wide text-slate-500">{f.label}</div><div className="font-semibold text-slate-900">{fmtV(f.value)}</div></div>)}</div>
      <div className="mt-2 max-h-48 overflow-auto"><table className="w-full text-left text-[11px]"><thead><tr>{r.columns.map((c) => <th key={c.key} className="border-b px-1 py-1 font-medium text-slate-500">{c.label}</th>)}</tr></thead>
        <tbody>{r.rows.length === 0 && <tr><td colSpan={r.columns.length} className="px-1 py-2 text-slate-400">No data for this period.</td></tr>}
          {r.rows.slice(0, 10).map((row, i) => <tr key={i}>{r.columns.map((c) => <td key={c.key} className="border-b border-slate-100 px-1 py-1">{fmtV(row[c.key])}</td>)}</tr>)}</tbody></table></div>
      {r.rows.length > 10 && <div className="mt-1 text-slate-400">Showing 10 of {r.rows.length}. Export for all rows.</div>}
      <Button size="sm" variant="outline" className="mt-2" data-testid="agyin-analytics-export" onClick={() => downloadCsv(r)}><Download className="mr-1 h-3.5 w-3.5" />Export CSV</Button>
    </div>);
}

export function KbProposeCard({ a }) {
  const e = a.entry || {};
  return (
    <div className={box} style={border} data-testid="agyin-kb-propose-card">
      <div className="flex items-center gap-1.5 font-semibold text-slate-900"><BookOpen className="h-3.5 w-3.5" />Suggested knowledge base entry · {e.status === "active" ? "already active" : "pending admin approval"}</div>
      <div className="mt-1 font-medium">{e.title}</div>
      <p className="mt-1 whitespace-pre-wrap text-slate-600">{e.body}</p>
      {!!e.tags?.length && <div className="mt-1 text-slate-500">Tags: {e.tags.join(", ")}</div>}
      <div className="mt-1 text-slate-500">A workspace admin can approve it in Settings, Knowledge Base.</div>
    </div>);
}

export function PinnedReview({ review }) {
  const [open, setOpen] = useState(false);
  if (!review) return null;
  return (
    <div className="border-b px-4 py-3" style={border} data-testid="agyin-pinned-review">
      <button className="flex w-full items-center gap-1.5 text-left text-xs font-semibold text-slate-800" data-testid="agyin-pinned-review-toggle" onClick={() => setOpen(!open)}>
        <Pin className="h-3.5 w-3.5 text-[#2B7F77]" />Weekly ops review · week of {review.week_of}{open ? <ChevronDown className="ml-auto h-3.5 w-3.5" /> : <ChevronRight className="ml-auto h-3.5 w-3.5" />}</button>
      {open && <div className="mt-2 text-sm" data-testid="agyin-pinned-review-body"><Markdown text={review.content} /></div>}
    </div>);
}

const BRIEF_ROWS = [["jobs_in_progress", "Jobs in progress", (v) => v], ["overdue_jobs", "Overdue jobs", (v) => v.length], ["overdue_invoices", "Overdue invoices", (v) => `${v.count} (${v.amount.toLocaleString()})`],
  ["low_stock", "Low-stock items", (v) => v.length], ["pending_approvals", "Pending approvals", (v) => Object.values(v).reduce((s, n) => s + n, 0)]];

export function DailyBrief({ data, onNavigate }) {
  if (!data) return null;
  const b = data.brief || {};
  return (
    <div className="rounded-lg border px-3 py-2.5 text-xs" style={border} data-testid="agyin-daily-brief">
      <div className="po-label mb-1.5">Daily brief · {b.date}</div>
      <dl className="space-y-1">{BRIEF_ROWS.filter(([k]) => b[k] !== undefined).map(([k, label, f]) => <div key={k} className="flex justify-between"><dt className="text-slate-500">{label}</dt><dd className="font-semibold text-slate-800" data-testid={`brief-${k}`}>{f(b[k])}</dd></div>)}</dl>
      <div className="mt-2 border-t pt-2" style={border}>
        {data.pro ? <div data-testid="brief-pro-summary"><div className="flex items-center gap-1 font-semibold text-slate-800"><Sparkles className="h-3 w-3 text-[#2B7F77]" />Pro summary · {data.pro_summary?.insights || 0} insight(s)</div>
          {(data.pro_summary?.top || []).map((t, i) => <div key={i} className="text-slate-600">{t}</div>)}</div>
          : <><div className="mb-1 flex items-center gap-1 font-semibold text-slate-400"><Lock className="h-3 w-3" />Pro summary</div><ProLocked testId="brief-pro-summary-locked" onNavigate={onNavigate} /></>}
      </div>
    </div>);
}

export function ProInsights({ pro, onNavigate, onUsage, renderItems }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!pro) return <div className="border-b px-4 py-3" style={border} data-testid="agyin-pro-insights-locked"><div className="po-label mb-1.5 flex items-center gap-1 text-slate-400"><Lock className="h-3 w-3" />Pro insights</div><ProLocked testId="agyin-pro-locked" onNavigate={onNavigate} /></div>;
  const load = async () => { setBusy(true); try { const r = (await api.get("/agyin/insights")).data; setItems(r.items); onUsage?.(r.usage); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  return (
    <div className="border-b px-4 py-3" style={border} data-testid="agyin-pro-insights">
      <div className="flex items-center justify-between"><span className="po-label">Pro insights</span>
        <Button size="sm" variant="outline" data-testid="agyin-pro-insights-load" disabled={busy} onClick={load}>{busy ? "Loading…" : items ? "Refresh" : "Show insights"}</Button></div>
      {items && (items.length ? <div className="max-h-64 overflow-y-auto">{renderItems(items)}</div> : <div className="mt-2 text-xs text-slate-500" data-testid="agyin-pro-insights-empty">No insights right now.</div>)}
    </div>);
}
